import { motion, useReducedMotion } from 'framer-motion';

const EASE = [0.22, 1, 0.36, 1] as const;

const PROCESS_STEPS = [
    {
        title: 'Apply',
        desc: 'Complete the form below. You’ll hear back within 24–48 hours on whether you’ve been moved to the interview round.',
    },
    {
        title: 'Interview',
        desc: 'Schedule a brief interview with a LuxRally team member. This is where we see if the fit makes sense for both of us.',
    },
    {
        title: 'Sign Your Contract',
        desc: 'Review and sign. All agents are bound by the same terms — no exceptions, no modifications.',
    },
    {
        title: 'Complete the Training',
        desc: 'Work through the Industry Course, Practical Evaluation, and Systems Course. Each course has clear timelines and deadlines.',
    },
    {
        title: 'Launch',
        desc: 'Set up your credentials, unlock access to our full community, and start making commissionable bookings — with a dedicated New Agent Sales Manager guiding you for your first six months.',
    },
];

export default function HowItWorks() {
    const shouldReduceMotion = useReducedMotion();

    return (
        <section id="how" className="agent-px scroll-mt-36 bg-[#0e2727] py-20 sm:py-28">
            <div className="mx-auto max-w-3xl">
                <motion.div
                    initial={shouldReduceMotion ? { opacity: 1 } : { opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true, margin: '-80px' }}
                    transition={{ duration: 0.7, ease: EASE }}
                    className="mb-12"
                >
                    <span className="lux-caps lux-caps-lg mb-4 block text-[#F5F5F5]/60">From Application to Production</span>
                    <h2 className="lux-didot lux-didot-lg text-[#F5F5F5]">How it works.</h2>
                </motion.div>

                <ol className="mt-4 divide-y divide-[#F5F5F5]/10 border-y border-[#F5F5F5]/10">
                    {PROCESS_STEPS.map((step, i) => (
                        <motion.li
                            key={i}
                            initial={shouldReduceMotion ? { opacity: 1 } : { opacity: 0, y: 12 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            viewport={{ once: true, margin: '-20px' }}
                            transition={{ duration: 0.5, delay: i * 0.08, ease: EASE }}
                            className="flex flex-col gap-3 py-8 sm:flex-row sm:items-start sm:gap-12"
                        >
                            <span className="lux-didot lux-didot-lg shrink-0 leading-none text-[#F5F5F5]/25 sm:w-20">
                                {String(i + 1).padStart(2, '0')}
                            </span>
                            <div className="sm:flex-1 sm:pt-2">
                                <h3 className="lux-body lux-body-lg mb-2 font-medium text-[#F5F5F5]">{step.title}</h3>
                                <p className="lux-body lux-body-sm leading-relaxed text-[#F5F5F5]/55">{step.desc}</p>
                            </div>
                        </motion.li>
                    ))}
                </ol>

                <motion.p
                    className="lux-body lux-body-sm mt-10 text-[#F5F5F5]/60"
                    initial={shouldReduceMotion ? { opacity: 1 } : { opacity: 0 }}
                    whileInView={{ opacity: 1 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.5, delay: 0.3, ease: EASE }}
                >
                    Most new agents graduate within one month.
                </motion.p>

                <motion.div
                    className="mt-8 rounded-xl border border-[#F5F5F5]/15 bg-[#0b2222] p-6"
                    initial={shouldReduceMotion ? { opacity: 1 } : { opacity: 0, y: 12 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.6, delay: 0.2, ease: EASE }}
                >
                    <p className="lux-body lux-body-sm leading-relaxed text-[#F5F5F5]/75">
                        <strong className="text-[#F5F5F5]">Already producing over $1M a year?</strong> Skip the Industry Course and Practical Exam. You
                        just need to take our Systems course and complete Orientation, which covers our proprietary systems. You can be live in as
                        little as 24 hours.
                    </p>
                </motion.div>
            </div>
        </section>
    );
}
